// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import clsx from 'clsx';
import history from 'src/history';
import { DefaultButton, IComboBoxOption } from 'office-ui-fabric-react';
import { useTranslation } from 'react-i18next';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {
    resetSelectedShippingOfferData,
    resetSelectedOffer,
    Progress,
    getShippingDeviceOfferData,
    getShippingAccessoryOfferData,
    reActivateOrderStatusSelector,
    reasonForNotSelectingOffers,
    offerReasonsSelector,
} from './../../../store/orderCreation';
import {
    updateCreateOrderProgress,
    resetRegistrationData,
    GET_REGISTER_INFO_DATA,
    resetPartsStatus,
    resetParts,
} from './../../../store/orderCreation/actions';
import SelectedRepairItemsDetails from './SelectedRepairItemsDetails';
import {
    allSelectedOfferSelector,
    isOnSiteRepairStatusSelector,
    parentDeviceOfferSelector,
    partAndAccessoryOfferSelector,
    secondSRCreationSelector,
    softwareRepairOfferSelector
} from './../../../store/orderCreation/selectors';
import { resetActionsData } from './../../../store/actionStatus';

import DeviceServiceOptions from './ServiceOptions';

import DoNotRepairReasonSelection from './DoNotRepairReasonSelection';

import Styles from './styles.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';
import { clearSafetyAttachmentInfo, CLEAR_SAFETY_ATTACHMENT_INFO } from './../../../store/attachments';
import { getOnsiteSoldToShipTo } from './../../../store/managePartners';

export default React.forwardRef(RepairOffer);

function RepairOffer(props: any, ref: any) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const selectedRepairOffers = useSelector(state => allSelectedOfferSelector(state));
    const parentDeviceOffer: any = useSelector(state => parentDeviceOfferSelector(state));
    const softwareOrderOffer: any = useSelector(state => softwareRepairOfferSelector(state));
    const partAndAccessoryOffers: [] = useSelector(state => partAndAccessoryOfferSelector(state));
    const reActivateOrder = useSelector(state => reActivateOrderStatusSelector(state));
    const [isDoNotReapirSelected, setDoNotReapirSelected] = React.useState(false);
    const offerReasonForNotRepairng = useSelector(state => offerReasonsSelector(state));
    const secondSRCreation = useSelector(state => secondSRCreationSelector(state));
    const isOnSiteRepair = useSelector(state => isOnSiteRepairStatusSelector(state));

    const [repairComments, setrepairComments] = React.useState(offerReasonForNotRepairng?.repairComments);
    const [repairReason, setrepairReason] = React.useState<IComboBoxOption | undefined>(offerReasonForNotRepairng?.repairReason);

    function onChangeSelectionClick() {
        logPageAction('Repair offers change selection button clicked');

        dispatch(resetSelectedOffer());
        dispatch(resetRegistrationData());
        dispatch(resetActionsData(GET_REGISTER_INFO_DATA));
        dispatch(updateCreateOrderProgress(Progress.ItemSelection));
        dispatch(resetPartsStatus());
        dispatch(resetParts());
        dispatch(clearSafetyAttachmentInfo());
        dispatch(resetActionsData(CLEAR_SAFETY_ATTACHMENT_INFO));

        if(secondSRCreation){
            history.push(`/device/orderDetail`);
        }
    }

    function onParentCommentsChange(value: string | undefined) {
        setrepairComments(value);
    }

    function onParentReasonsChange(value: IComboBoxOption | undefined) {
        setrepairReason(value);
    }

    function onSubmit() {
        logPageAction('Continue to shipping address: Continue button clicked');
        dispatch(reasonForNotSelectingOffers({
            repairReason,
            repairComments,
        }));

        dispatch(updateCreateOrderProgress(Progress.UpdateDeviceNewSerialNumber));

        dispatch(getShippingDeviceOfferData());
        dispatch(getShippingAccessoryOfferData());
        dispatch(resetSelectedShippingOfferData());//RESET previously selected shipping offers

        if (isOnSiteRepair) {
            dispatch(getOnsiteSoldToShipTo());
        }
    }

    function numberOfItemHasOffer() {
        const numberOfPartAndAccessoryHasOffer = Array.isArray(partAndAccessoryOffers) ? partAndAccessoryOffers.filter((item: any) => item.offers.length > 0).length : 0;
        const parentHasOffer = parentDeviceOffer && parentDeviceOffer.hasOwnProperty('offers') && parentDeviceOffer.offers.length > 0 ? 1 : 0;
        const softwareOrderHasOffer = (softwareOrderOffer && softwareOrderOffer.hasOwnProperty('offers') && softwareOrderOffer.offers.length > 0) ? 1 : 0;
        return numberOfPartAndAccessoryHasOffer + parentHasOffer + softwareOrderHasOffer;

    }

    function isFormSubmittable() {
        if (!reActivateOrder) {
            return (
                selectedRepairOffers &&
                Object.keys(selectedRepairOffers).length === numberOfItemHasOffer() &&
                Object.values(selectedRepairOffers).reduce((hasValue: boolean, offer: any) => hasValue || Object.keys(offer).length > 1, false)
            );
        }
        let isSubmit = selectedRepairOffers && Object.keys(selectedRepairOffers).length === numberOfItemHasOffer();
        if (isSubmit) {
            const allDoNotRepairOffersNotSelected = selectedRepairOffers &&
                Object.values(selectedRepairOffers).reduce((hasValue: boolean, offer: any) => hasValue || Object.keys(offer).length > 1, false);
            if (!isDoNotReapirSelected && !allDoNotRepairOffersNotSelected) {
                setDoNotReapirSelected(true);
            } else if (isDoNotReapirSelected && allDoNotRepairOffersNotSelected) {
                setDoNotReapirSelected(false);
                setrepairComments('');
                setrepairReason(undefined);
            } else if (isDoNotReapirSelected) {
                if (typeof repairComments === 'undefined' || repairComments === '' || typeof repairReason === 'undefined') {
                    isSubmit = false;
                }
            }
        }
        return isSubmit;
    }

    const isContinueDisabled: boolean = !isFormSubmittable();

    const btnClassName = clsx(Styles.continueButton, {
        [Styles.disabled]: isContinueDisabled,
    });

    return (
        <div id="select_repair_items" className={Styles.repairItemContainer}>
            <header className={Styles.headerSection}>
                <h2 className={Styles.h2Heading}>{t('select_repair_items')}</h2>
                {!reActivateOrder &&
                    <DefaultButton
                        ariaLabel={t('change_repair_item_selection')}
                        className={Styles.changeSelection}
                        text={t('change_selection')}
                        onClick={onChangeSelectionClick}
                    />
                }
            </header>
            <SelectedRepairItemsDetails />
            <section className={Styles.offerTableContainer}>
                <DeviceServiceOptions readonly={props.readonly} ref={ref} />
            </section>
            {reActivateOrder && isDoNotReapirSelected && (
                <section>
                    <DoNotRepairReasonSelection
                        repairComments={repairComments}
                        repairReason={repairReason}
                        onParentCommentsChange={onParentCommentsChange}
                        onParentReasonsChange={onParentReasonsChange}
                        readonly={props.readonly}
                    />
                </section>
            )}
            {!props.readonly && (
                <section className={Styles.continueButtonContainer}>
                    <DefaultButton
                        className={btnClassName}
                        text={t('continue')}
                        disabled={isContinueDisabled}
                        onClick={onSubmit}
                    />
                </section>
            )}
        </div>
    );
}

